import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { COLORS, FONTS } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import SiliconeButton from "@/components/SiliconeButton";
import StoneTotem from "@/components/StoneTotem";
import StudioDownloadPanel from "@/components/StudioDownloadPanel";
import ContributionModal from "@/components/ContributionModal";

export default function Profile() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [contribOpen, setContribOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const p = await api.getProfile();
        setProfile(p);
      } catch (e) {
        console.warn("profile load", e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const upgrade = profile?.upgrade;
  const typeColor = upgrade?.body_color || COLORS.library;
  const isMaster = user?.plan === "master";

  return (
    <div data-testid="profile-screen" style={{
      minHeight: "100vh", background: COLORS.bg,
      padding: "24px 20px 80px", fontFamily: FONTS.body,
    }}>
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        {/* Top row — stone back */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
          <button
            onClick={() => navigate(-1)}
            data-testid="profile-back"
            style={{
              display: "inline-flex", alignItems: "center", gap: 12,
              background: "transparent", border: "none", padding: 0, cursor: "pointer",
              color: "var(--fg-55)",
              fontFamily: '"JetBrains Mono", ui-monospace, monospace',
              fontSize: 11, letterSpacing: "0.24em", textTransform: "uppercase",
            }}
          >
            <span style={{
              width: 14, height: 14, borderRadius: "50%",
              background: "radial-gradient(34% 30% at 32% 26%, #fff, #8AFFC4 22%, #05060c 100%)",
              boxShadow: "0 0 12px rgba(138,255,196,0.8)",
            }} />
            <span>back</span>
          </button>
          <div style={{ color: COLORS.textMuted, fontSize: 11, letterSpacing: 3, textTransform: "uppercase" }}>
            You
          </div>
        </div>

        {loading ? (
          <div style={{ color: COLORS.textMuted, padding: 20 }}>Loading…</div>
        ) : (
          <>
            {/* --------- HERO — user card with optional stone totem --------- */}
            <div data-testid="profile-hero" style={{
              position: "relative",
              padding: 26,
              borderRadius: 26,
              background: `linear-gradient(135deg, ${typeColor}22 0%, ${COLORS.surface} 60%)`,
              border: `1px solid ${typeColor}55`,
              boxShadow: `inset 0 1px 0 ${typeColor}55, 0 0 32px -10px ${typeColor}66`,
              backdropFilter: "blur(14px)",
              marginBottom: 20,
              overflow: "hidden",
            }}>
              {/* decorative glow */}
              <div style={{
                position: "absolute", top: -60, right: -60, width: 220, height: 220,
                background: `radial-gradient(circle, ${typeColor}22 0%, transparent 70%)`,
                pointerEvents: "none",
              }} />
              <div style={{ display: "flex", alignItems: "center", gap: 18, position: "relative" }}>
                {user?.picture ? (
                  <img
                    src={user.picture}
                    alt=""
                    style={{
                      width: 64, height: 64, borderRadius: 32,
                      border: `2px solid ${typeColor}88`,
                      boxShadow: `0 0 20px -4px ${typeColor}88`,
                    }}
                  />
                ) : (
                  <div style={{
                    width: 64, height: 64, borderRadius: 32,
                    background: `linear-gradient(135deg, ${typeColor}, ${COLORS.mirror})`,
                    display: "grid", placeItems: "center",
                    color: "#0D0D1A", fontFamily: FONTS.heading, fontSize: 22, fontWeight: 800,
                  }}>{(user?.name || "?")[0]}</div>
                )}

                {upgrade && (
                  <StoneTotem color={typeColor} size={52} title={upgrade.type_name_en} />
                )}

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    color: COLORS.textPrimary, fontFamily: FONTS.heading,
                    fontSize: 24, letterSpacing: -0.4, fontWeight: 700, marginBottom: 2,
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                  }}>
                    {user?.name || "Traveller"}
                  </div>
                  <div style={{ color: COLORS.textMuted, fontSize: 12, marginBottom: 8 }}>
                    {user?.email}
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <span style={{
                      padding: "3px 10px", borderRadius: 999,
                      background: `rgba(var(--role-blue-rgb),0.13)`, border: `1px solid rgba(var(--role-blue-rgb),0.40)`,
                      color: COLORS.mirror, fontSize: 10, fontWeight: 700, letterSpacing: 1,
                    }}>
                      {(user?.plan || "free").toUpperCase()}
                    </span>
                    {upgrade && (
                      <span data-testid="profile-type-chip" style={{
                        padding: "3px 10px", borderRadius: 999,
                        background: `${typeColor}22`, border: `1px solid ${typeColor}88`,
                        color: typeColor, fontSize: 10, fontWeight: 700, letterSpacing: 1,
                      }}>
                        TYPE {String(upgrade.type_id).padStart(2, "0")} · {(upgrade.type_name_en || "").toUpperCase()}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* --------- YUO Studio download / install (prominent, near name) --------- */}
            {isMaster ? <StudioDownloadPanel /> : null}

            {/* --------- PLAN row --------- */}
            <div style={{
              padding: 20, borderRadius: 20,
              background: COLORS.surface, border: `1px solid ${COLORS.border}`,
              marginBottom: 20, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
            }}>
              <div>
                <div style={{ color: COLORS.textMuted, fontSize: 11, letterSpacing: 2, textTransform: "uppercase", marginBottom: 6 }}>
                  Plan
                </div>
                <div style={{ color: COLORS.textPrimary, fontFamily: FONTS.heading, fontSize: 20 }}>
                  {(user?.plan || "free") === "master" ? "YUO Master" :
                   (user?.plan || "free") === "pro"    ? "YUO Pro"    : "YUO Free"}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
                <SiliconeButton
                  testId="open-paywall-btn"
                  color={COLORS.diary}
                  size="md"
                  onClick={() => navigate("/paywall")}
                >
                  See plans
                </SiliconeButton>
                <button
                  data-testid="open-contribution-btn"
                  onClick={() => setContribOpen(true)}
                  style={{
                    fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase",
                    padding: "10px 20px", borderRadius: 999, border: "none", cursor: "pointer",
                    background: "radial-gradient(120% 160% at 30% 0%, rgba(169,183,206,0.30), rgba(169,183,206,0.09))",
                    color: "var(--yuo-ink)", boxShadow: "0 0 24px rgba(169,183,206,0.24)",
                  }}
                >
                  Contribution
                </button>
              </div>
            </div>
            <ContributionModal open={contribOpen} onClose={() => setContribOpen(false)} lang="en" />

            {/* --------- UPGRADE card --------- */}
            {upgrade ? (
              <UpgradeCollapsed
                upgrade={upgrade}
                typeColor={typeColor}
                expanded={expanded}
                setExpanded={setExpanded}
                navigate={navigate}
                isMaster={isMaster}
              />
            ) : (
              <div
                data-testid="open-upgrade-card"
                onClick={() => navigate("/personalization")}
                style={{
                  cursor: "pointer",
                  background: `linear-gradient(135deg, rgba(var(--role-yellow-rgb),0.13) 0%, rgba(var(--role-purple-rgb),0.13) 60%, ${COLORS.surface} 100%)`,
                  border: `1px solid rgba(var(--role-yellow-rgb),0.40)`,
                  borderRadius: 22, padding: 26, marginBottom: 20,
                  boxShadow: `0 0 32px -8px rgba(var(--role-yellow-rgb),0.27)`,
                }}
              >
                <div style={{ color: COLORS.library, fontSize: 11, letterSpacing: 3, textTransform: "uppercase", marginBottom: 8 }}>
                  YUO · Conscious Upgrade
                </div>
                <div style={{
                  color: COLORS.textPrimary, fontFamily: FONTS.heading, fontSize: 30,
                  letterSpacing: -0.5, marginBottom: 8,
                }}>
                  Personalization →
                </div>
                <div style={{ color: COLORS.textSecondary, fontSize: 14, lineHeight: "22px", marginBottom: 14 }}>
                  Take the YUO Personalization diagnostic — a conscious Upgrade for your growth: your body, thinking style, dominant gland, current IQ level, plus practices to grow.
                </div>
                <SiliconeButton
                  testId="start-upgrade"
                  color={COLORS.library}
                  onClick={(e) => { e.stopPropagation(); navigate("/personalization"); }}
                >
                  Begin the journey
                </SiliconeButton>
              </div>
            )}

            {/* --------- Sign out --------- */}
            <div style={{ marginTop: 24, display: "flex", justifyContent: "center" }}>
              <SiliconeButton
                testId="logout-button"
                color={COLORS.error}
                variant="ghost"
                size="md"
                onClick={async () => {
                  await signOut();
                  navigate("/login", { replace: true });
                }}
              >
                Sign out
              </SiliconeButton>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// -------------------------------------------------------------------
// Collapsed/expanded UPGRADE result card
// -------------------------------------------------------------------
function UpgradeCollapsed({ upgrade, typeColor, expanded, setExpanded, navigate, isMaster }) {
  const label = (s) => (
    <div style={{ color: COLORS.textMuted, fontSize: 10, textTransform: "uppercase",
      letterSpacing: 1.5, marginBottom: 6 }}>{s}</div>
  );
  return (
    <div
      data-testid="upgrade-result-card"
      style={{
        borderRadius: 22, marginBottom: 20,
        background: `linear-gradient(135deg, ${typeColor}22 0%, ${COLORS.surface} 60%)`,
        border: `1px solid ${typeColor}66`,
        boxShadow: `0 0 32px -10px ${typeColor}66`,
        overflow: "hidden",
      }}
    >
      {/* header row — always visible */}
      <div
        data-testid="upgrade-toggle"
        onClick={() => setExpanded((x) => !x)}
        style={{
          display: "flex", alignItems: "center", gap: 16,
          padding: 20, cursor: "pointer",
        }}
      >
        <StoneTotem color={typeColor} size={56} title={upgrade.type_name_en} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: typeColor, fontSize: 11, letterSpacing: 3, textTransform: "uppercase", marginBottom: 4 }}>
            Your YUO type · IQ {upgrade.iq_level}
          </div>
          <div style={{
            color: COLORS.textPrimary, fontFamily: FONTS.heading, fontSize: 20, letterSpacing: -0.3,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {String(upgrade.type_id).padStart(2, "0")} · {upgrade.type_name_en}
          </div>
          <div style={{ color: COLORS.textSecondary, fontSize: 12, marginTop: 2 }}>
            {upgrade.thinking_en}
          </div>
        </div>
        <div style={{ color: typeColor, fontSize: 22, transform: expanded ? "rotate(180deg)" : "rotate(0)", transition: "transform 240ms" }}>
          ⌄
        </div>
      </div>

      {/* expanded body */}
      {expanded && (
        <div data-testid="upgrade-expanded" style={{ padding: "0 20px 22px", display: "grid", gap: 16 }}>
          {upgrade.summary && (
            <div style={{
              background: COLORS.bg, border: `1px solid ${COLORS.border}`,
              borderRadius: 16, padding: 18, fontSize: 15, lineHeight: "24px",
              color: COLORS.textPrimary, whiteSpace: "pre-wrap",
            }}>
              {upgrade.summary}
            </div>
          )}

          {upgrade.strengths?.length ? (
            <Section title="Strengths" color={typeColor}>
              <List items={upgrade.strengths} />
            </Section>
          ) : null}
          {upgrade.shadows?.length ? (
            <Section title={`Shadows at ${upgrade.iq_level} IQ`} color={typeColor}>
              <List items={upgrade.shadows} />
            </Section>
          ) : null}
          {upgrade.practices?.length ? (
            <Section title="Practices to climb" color={typeColor}>
              <List items={upgrade.practices} />
            </Section>
          ) : null}
          {upgrade.growth_arc && (
            <Section title="Growth arc" color={typeColor}>
              <div style={{ color: COLORS.textPrimary, fontSize: 14, lineHeight: "22px" }}>
                {upgrade.growth_arc}
              </div>
            </Section>
          )}

          <div style={{
            marginTop: 4, padding: 18, borderRadius: 16,
            background: `linear-gradient(180deg, ${typeColor}18 0%, ${COLORS.bg} 100%)`,
            border: `1px solid ${typeColor}66`,
          }}>
            <div style={{ color: typeColor, fontSize: 10, letterSpacing: 2, textTransform: "uppercase", marginBottom: 8 }}>
              Personal · Path of Growth
            </div>
            <div style={{ color: COLORS.textPrimary, fontFamily: FONTS.heading, fontSize: 16, marginBottom: 6 }}>
              Insights live in your Diary now
            </div>
            <div style={{ color: COLORS.textSecondary, fontSize: 13, lineHeight: "20px", marginBottom: 14 }}>
              Every time you write, YUO reads it through the lens of your {upgrade.type_name_en} type and leaves a personal insight coloured in your totem. Open Diary → Insights.
            </div>
            <SiliconeButton
              testId="open-diary-insights"
              color={typeColor}
              onClick={() => navigate("/diary?view=insights")}
            >
              Open Insights →
            </SiliconeButton>
          </div>

          {!isMaster && (
            <div data-testid="master-invite" style={{
              padding: 18, borderRadius: 16,
              background: `linear-gradient(135deg, rgba(var(--role-purple-rgb),0.13) 0%, rgba(var(--role-yellow-rgb),0.13) 100%)`,
              border: `1px dashed rgba(var(--role-purple-rgb),0.53)`,
            }}>
              <div style={{ color: COLORS.diary, fontSize: 10, letterSpacing: 2, textTransform: "uppercase", marginBottom: 8 }}>
                When you are ready · Master
              </div>
              <div style={{ color: COLORS.textPrimary, fontFamily: FONTS.heading, fontSize: 17, marginBottom: 6 }}>
                A deeper reading awaits.
              </div>
              <div style={{ color: COLORS.textSecondary, fontSize: 13, lineHeight: "20px", marginBottom: 14 }}>
                YUO Master unlocks the full depth of Personalization: multi-layer somatic mapping, dialogue with your shadow, and a living portrait that evolves with every conversation. When you feel ready — this door will open.
              </div>
              <SiliconeButton
                testId="open-master"
                color={COLORS.diary}
                onClick={() => navigate("/paywall?plan=master&feature=personalization_deep")}
              >
                See Master →
              </SiliconeButton>
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "center", marginTop: 4 }}>
            <SiliconeButton
              testId="retake-upgrade"
              color={COLORS.textSecondary}
              variant="ghost"
              size="sm"
              onClick={() => navigate("/personalization")}
            >
              Retake diagnostic
            </SiliconeButton>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, color, children }) {
  return (
    <div>
      <div style={{
        color, fontSize: 10, textTransform: "uppercase", letterSpacing: 2, marginBottom: 8,
      }}>{title}</div>
      {children}
    </div>
  );
}

function List({ items }) {
  return (
    <ul style={{
      margin: 0, padding: 0, listStyle: "none", display: "grid", gap: 6,
    }}>
      {items.map((s, i) => (
        <li key={i} style={{
          padding: "10px 14px", borderRadius: 12,
          background: "var(--fg-06)",
          border: `1px solid ${COLORS.border}`,
          color: COLORS.textPrimary, fontSize: 14, lineHeight: "20px",
        }}>{s}</li>
      ))}
    </ul>
  );
}
